import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthKlaviyoDialog } from "./AuthKlaviyoDialog";
import { useValidateKlaviyo, useGetKlaviyoStatus } from "hooks";
import { PermissionAlertDialog } from "components";

export function BrowseKlaviyoCard({ userProfile }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const [isApiKeyValid, setIsApiKeyValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const validateKlaviyo = useValidateKlaviyo();
  const { data: klaviyoStatusData, isLoading: isStatusLoading } = useGetKlaviyoStatus(userProfile.organisation_id);

  const isKlaviyoActive = () => {
    return klaviyoStatusData?.data?.is_active === true;
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) {
      handleCancelAuthDialog();
    }
  };

  const handleConnectClick = () => {
    // Check if user is owner
    const isOwner = userProfile.user_role === "Owner";
    
    if (isOwner) {
      handleOpenChange(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  const handleCancelAuthDialog = () => {
    setApiKey("");
    setIsApiKeyValid(true);
  };

  const handleApiKeyChange = (event) => {
    setApiKey(event.target.value);
    setIsApiKeyValid(true);
  };

  const handleSave = async () => {
    let isValid = true;

    if (!apiKey) {
      setIsApiKeyValid(false);
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    setIsLoading(true);
    setProgressValue(10);

    try {
      const result = await validateKlaviyo.mutateAsync({
        api_key: apiKey,
        organisation_id: userProfile.organisation_id,
      });

      setProgressValue(50);

      if (result?.data?.status === "success") {
        navigate("/klaviyo-setup", {
          state: {
            apiKey,
            orgId: userProfile.organisation_id,
            lists: result.data.lists,
          },
        });
      } else {
        setIsApiKeyValid(false);
      }
    } catch (error) {
      console.error("Validation error:", error);

      const invalidField = error?.response?.data?.invalid;
      const status = error?.response?.status;

      if (invalidField === "api_key" || status === 401 || status === 403) {
        setIsApiKeyValid(false);
      } else {
        // showToast("Unable to validate right now — please try again later.");
      }
    } finally {
      setIsLoading(false);
      setProgressValue(0);
    }
  };

  return (
    <>
      <Card style={{ display: "flex", width: "100%", minWidth: 0, padding: "var(--space-4)", justifyContent: "space-between",
        alignItems: "center", gap: "var(--space-3)", overflow: "hidden" }}>
        {/* Klaviyo integration name and logo */}
        <Flex align="center" gap="4" style={{ minWidth: 0, flex: "1 1 auto" }}>
          <Flex height="40px" width="40px" p="2"
            style={{ flexShrink: 0, borderRadius: "var(--space-1)", background: "#FFFFFF", border: "1px solid var(--gray-a6)" }}>
            <img src={require("../../../assets/images/klaviyo.jpg")} alt="klaviyo" style={{ width: "24px" }} />
          </Flex>
          <Flex direction="column" justify="center" align="start" style={{ minWidth: 0 }}>
            <Text size="4" weight="medium" truncate>Klaviyo</Text>
            <Text size="1" color="gray" truncate>CRM & Email marketing</Text>
          </Flex>
        </Flex>

        {/* Klaviyo integration status and action button */}
        <Flex align="center" style={{ flexShrink: 0 }}>
          {isKlaviyoActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthKlaviyoDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={handleCancelAuthDialog}
        onSave={handleSave}
        handleApiKeyChange={handleApiKeyChange}
        isApiKeyValid={isApiKeyValid}
        isLoading={isLoading}
        progressValue={progressValue}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
